import { contractService } from '@app/shared/services/contract.service';
import type {
  ContractStatusResponse,
  ContractVersion,
  CreateContractVersionDto,
  SignContractDto,
  UserContractSignature,
} from '@neogeniux/api-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const contractKeys = {
  all: ['contracts'] as const,
  current: () => [...contractKeys.all, 'current'] as const,
  status: () => [...contractKeys.all, 'status'] as const,
  history: () => [...contractKeys.all, 'history'] as const,
  versions: () => [...contractKeys.all, 'versions'] as const,
};

export const useCurrentContract = (enabled = true) =>
  useQuery<ContractVersion | null>({
    queryKey: contractKeys.current(),
    queryFn: () => contractService.getCurrent(),
    enabled,
  });

export const useContractStatus = (enabled = true) =>
  useQuery<ContractStatusResponse>({
    queryKey: contractKeys.status(),
    queryFn: () => contractService.getMyStatus(),
    enabled,
    staleTime: 60 * 1000,
  });

export const useContractHistory = () =>
  useQuery<UserContractSignature[]>({
    queryKey: contractKeys.history(),
    queryFn: () => contractService.getMyHistory(),
    select: (data) => (Array.isArray(data) ? data : []),
  });

export const useSignContract = () => {
  const queryClient = useQueryClient();
  return useMutation<UserContractSignature, Error, SignContractDto>({
    mutationFn: (payload) => contractService.sign(payload),
    onSuccess: (sig) => {
      // Flip cached needsSigning=false SYNCHRONOUSLY so the next
      // AuthGuard render (triggered by contract-signing.page's
      // navigate(dashboard)) reads the fresh gate — invalidate alone
      // marks stale but returns to a stale cache until the refetch
      // resolves, which racedd the navigate and bounced the operator
      // back to /contract-signing for a beat. Same shape as the
      // seeded snapshot from handleAuthSuccess.
      const current = queryClient.getQueryData<ContractStatusResponse>(
        contractKeys.status()
      );
      queryClient.setQueryData<ContractStatusResponse>(contractKeys.status(), {
        needsSigning: false,
        currentVersion: current?.currentVersion ?? null,
        lastSignedVersion: sig.contractVersionSnapshot ?? null,
      });
      queryClient.invalidateQueries({ queryKey: contractKeys.history() });
    },
  });
};

// ─── sadmin ─────────────────────────────────────────────────────────────

export const useSadminContractVersions = () =>
  useQuery<ContractVersion[]>({
    queryKey: contractKeys.versions(),
    queryFn: () => contractService.listVersions(),
    select: (data) => (Array.isArray(data) ? data : []),
  });

export const useSadminCreateContractVersion = () => {
  const queryClient = useQueryClient();
  return useMutation<ContractVersion, Error, CreateContractVersionDto>({
    mutationFn: (payload) => contractService.createVersion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
};

export const useSadminActivateContractVersion = () => {
  const queryClient = useQueryClient();
  return useMutation<ContractVersion, Error, string>({
    mutationFn: (id) => contractService.activateVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
};
